/**
 * CreateInvitationCodePage
 * Create page for invitation codes
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/common/page-header";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useCreateInvitationCode } from "@/hooks/useInvitationCodeQueries";

const createInvitationCodeSchema = z.object({
  code: z
    .string()
    .min(1, "Code is required")
    .regex(/^[A-Za-z0-9]{4,20}$/, "Code must be 4-20 alphanumeric characters"),
  rewardAmount: z.number().min(1, "Reward amount must be positive"),
  maxUses: z
    .union([z.number().int().positive(), z.literal(-1)])
    .optional()
    .nullable(),
  expiresInDays: z
    .number()
    .int("Days must be an integer")
    .min(1, "Days must be at least 1")
    .max(365, "Days must not exceed 365")
    .optional()
    .nullable(),
  description: z.string().max(500).optional().nullable(),
});

type CreateInvitationCodeForm = z.infer<typeof createInvitationCodeSchema>;

function generateRandomCode(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "";
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

export function CreateInvitationCodePage() {
  const navigate = useNavigate();
  const createMutation = useCreateInvitationCode();
  const [unlimitedUses, setUnlimitedUses] = useState(false);

  const form = useForm<CreateInvitationCodeForm>({
    resolver: zodResolver(createInvitationCodeSchema),
    defaultValues: {
      code: generateRandomCode(),
      rewardAmount: 50000,
      maxUses: 10,
      expiresInDays: 30,
      description: "",
    },
  });

  const onSubmit = (data: CreateInvitationCodeForm) => {
    const validTo = data.expiresInDays
      ? new Date(
          Date.now() + data.expiresInDays * 24 * 60 * 60 * 1000,
        ).toISOString()
      : undefined;

    const payload = {
      code: data.code,
      rewardAmount: data.rewardAmount,
      currency: "VND",
      maxUses: unlimitedUses ? null : data.maxUses,
      allowExistingUsers: false,
      validTo,
      description: data.description || undefined,
    };

    createMutation.mutate(payload, {
      onSuccess: (response) => {
        navigate(`/admin/invitation-codes/${response.id}`);
      },
    });
  };

  const regenerateCode = () => {
    form.setValue("code", generateRandomCode());
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Create Invitation Code"
        subtitle="Generate a new invitation code for user registration"
        back={{
          label: "Back to Invitation Codes",
          onClick: () => navigate("/admin/invitation-codes"),
        }}
      />

      {/* Form Card */}
      <Card>
        <CardHeader>
          <CardTitle>Invitation Code Settings</CardTitle>
          <CardDescription>
            Configure the invitation code parameters
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Code */}
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code</FormLabel>
                    <FormDescription>
                      4-20 alphanumeric characters. Users will enter this code
                      during registration.
                    </FormDescription>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <Input
                          placeholder="e.g., WELCOME2024"
                          className="font-mono uppercase"
                          {...field}
                          onChange={(e) =>
                            field.onChange(e.target.value.toUpperCase())
                          }
                        />
                      </FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={regenerateCode}
                        title="Generate random code"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Reward Amount */}
              <FormField
                control={form.control}
                name="rewardAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reward Amount (VND)</FormLabel>
                    <FormDescription>
                      Account credit awarded to users who register with this
                      code
                    </FormDescription>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value) || 0)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Max Uses */}
              <FormField
                control={form.control}
                name="maxUses"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Maximum Uses</FormLabel>
                    <FormDescription>
                      The maximum number of times this code can be used
                    </FormDescription>
                    <div className="flex items-center gap-4">
                      {unlimitedUses ? (
                        <div className="flex-1">
                          <Input value="Unlimited" disabled />
                        </div>
                      ) : (
                        <FormControl>
                          <Input
                            type="number"
                            min="1"
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseInt(e.target.value) || 1)
                            }
                          />
                        </FormControl>
                      )}
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={unlimitedUses}
                          onCheckedChange={(checked) => {
                            setUnlimitedUses(checked);
                            if (checked) {
                              field.onChange(null);
                            } else {
                              field.onChange(10);
                            }
                          }}
                        />
                        <span className="text-sm text-muted-foreground">
                          Unlimited
                        </span>
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Expires In Days */}
              <FormField
                control={form.control}
                name="expiresInDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiration (Days)</FormLabel>
                    <FormDescription>
                      Number of days before this invitation code expires. Leave
                      empty for no expiration.
                    </FormDescription>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        max="365"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value) || undefined)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (optional)</FormLabel>
                    <FormDescription>
                      Internal note about this invitation code
                    </FormDescription>
                    <FormControl>
                      <Input
                        placeholder="e.g., Marketing campaign Q1 2026"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Actions */}
              <div className="flex items-center justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/admin/invitation-codes")}
                  disabled={createMutation.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Invitation Code"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
